import { useOrganization, useUser } from '@clerk/clerk-react'

import { ProtectedRoute } from '@/components/ProtectedRoute'
import { useConfiguration } from '../hooks/useConfiguration'
import type { FeatureFlags } from '@/types/configuration'
import type { ReactNode } from 'react'

interface ProtectedFeatureRouteProps {
  children: ReactNode
  /** Org-level feature flag that must be enabled for the page to open. */
  featureFlag: keyof FeatureFlags
}

/**
 * Pages gated purely by an organization feature flag, with no role attached.
 *
 * `ProtectedGlobalAdminRoute` can also take a flag, but it requires global
 * admin on top of it; this is the flag on its own, for a page being rolled out
 * per organization rather than restricted to staff.
 *
 * `allowed` stays `undefined` until Clerk and the configuration have settled,
 * so a page mid-rollout does not flash the denial toast before its flag
 * arrives. `isPreviousOrgData` counts as unsettled: during an org switch the
 * config query serves the org being *left* under the new org's id with
 * `loading: false`, so without it this would answer from the wrong org's flag
 * and keep the page open until the real one landed.
 */
export function ProtectedFeatureRoute({
  children,
  featureFlag,
}: ProtectedFeatureRouteProps) {
  const { isLoaded: userLoaded } = useUser()
  const { isLoaded: orgLoaded } = useOrganization()
  const {
    configuration,
    loading: configLoading,
    error,
    isPreviousOrgData,
  } = useConfiguration()

  const ready = userLoaded && orgLoaded && !configLoading && !isPreviousOrgData

  // A settled fetch failure still denies — holding the spinner forever is worse
  // — but it is not the same fact as "your organization does not have this", so
  // it does not claim to be. `useConfiguration` retries before reporting one.
  const lookupFailed = ready && error !== null

  return (
    <ProtectedRoute
      allowed={ready ? !!configuration?.features?.[featureFlag] : undefined}
      message={
        lookupFailed
          ? 'Could not check which features your organization has. Please try again.'
          : 'This page is not enabled for your organization.'
      }
      toastId="feature-not-enabled">
      {children}
    </ProtectedRoute>
  )
}
